import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// Reusable "make this floating button drag-and-droppable" hook.
//
// Returns:
//   ref      — attach to the floating element (the button itself)
//   style    — inline style object with the current position
//   dragging — true while the user is actively dragging
//
// Behavior (rewritten Feb 2026 — old long-press model felt broken):
//   • Tap = click (passes through to onClick).
//   • Press + drag past 2px = pick up immediately, no timer to wait for.
//   • Position is clamped to the visible viewport on every move and
//     re-clamped on resize so an element placed in the corner of a
//     wide screen doesn't end up off-screen on a phone.
//   • Snaps to the nearest horizontal edge when released.
//   • Pointer Events API (so touch, mouse, and stylus all work with
//     one code path), with explicit mouse-event fallback for headless
//     test environments where pointerdown isn't dispatched.
//   • The element is forced to `touch-action: none` so the browser
//     doesn't try to scroll/zoom when you drag it.
//
// IMPORTANT: we use a *callback ref* backed by useState rather than
// useRef. Some consumers (MiniPlayer) conditionally render `null` on
// their first paint, so a useRef pointed at a not-yet-mounted div
// would leave the useEffect's listener-attach code bailing out on
// `if (!el) return;` and never re-running. With a callback ref, the
// state setter fires whenever the underlying DOM node attaches OR
// detaches, which re-runs the effect and binds listeners to the
// LIVE element every time.
export default function useDraggableFAB({
  storageKey,
  defaultPosition = { right: 20, bottom: 20 },
  size = 56,
  // Optional callback invoked at the end of a drag once the widget
  // has snapped to its nearest edge. Receives `{ edge, distance }`
  // where `edge` is "left"|"right"|"top"|"bottom" and `distance` is
  // how far (in px) the widget center was from that edge AT RELEASE
  // (before snapping). The MiniPlayer uses this to auto-tuck itself
  // into the edge whenever the user flings/drags it there — no
  // "Hide" button required.
  onSnap,
  // Pixel reserve at the bottom of the viewport that the widget can
  // never be dragged into (or snapped to). Use this when the page
  // has a sticky BottomNav that would otherwise cover the widget.
  // Default 0 (no reserve) so other callers keep their existing
  // behavior.
  bottomReserve = 0,
}) {
  const [el, setEl] = useState(null);
  const ref = useCallback((node) => setEl(node), []);
  const [dragging, setDragging] = useState(false);
  const [pos, setPos] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      // One-shot reset: bumped to v4 because the MiniPlayer's default
      // bottom position now reserves 96px for the BottomNav. Any saved
      // positions written before that reserve existed would still be
      // covering the BottomNav. Wipe once so users land on the new
      // safe default. Both the MiniPlayer and the Voice mic FAB share
      // this migration because both got the BottomNav-clearing fix
      // at the same time.
      const RESET_KEY = "hw:fab:position-reset-v4-2026-02-bottomnav";
      if ((storageKey === "hw:fab:miniplayer" || storageKey === "hw:fab:voice")
          && !window.localStorage.getItem(`${RESET_KEY}:${storageKey}`)) {
        window.localStorage.removeItem(storageKey);
        window.localStorage.setItem(`${RESET_KEY}:${storageKey}`, "1");
        return null;
      }
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const w = window.innerWidth, h = window.innerHeight;
      const bottomFloor = h - bottomReserve - 16;
      if (!parsed || typeof parsed.left !== "number" || typeof parsed.top !== "number"
          || parsed.left < -64 || parsed.left > w - 16
          || parsed.top < 80   || parsed.top > bottomFloor) {
        window.localStorage.removeItem(storageKey);
        return null;
      }
      return parsed;
    } catch (_) {}
    return null;
  });

  // Pointer tracking. Stored in refs so the move/up handlers don't
  // re-bind on every render.
  const stateRef = useRef({
    pointerId: null,
    startX: 0,
    startY: 0,
    elemX: 0,
    elemY: 0,
    dragging: false,
    moved: false,
  });

  // Disable native touch handling on the element so a drag doesn't
  // accidentally scroll the page. Applied via style on the live DOM
  // node so it can't be overridden by Tailwind class purging.
  useLayoutEffect(() => {
    if (!el) return;
    el.style.touchAction = "none";
    el.style.userSelect = "none";
    el.style.webkitUserSelect = "none";
  }, [el]);

  useEffect(() => {
    if (!el || typeof window === "undefined") return;

    const THRESHOLD = 2; // px before a press becomes a drag — as sensitive as the VOICE/AI FABs

    const onPointerDown = (e) => {
      // Only primary button or touch — let everything else pass through.
      if (e.pointerType === "mouse" && e.button !== 0) return;
      // Respect `data-no-drag` on any ancestor of the actual target so
      // buttons like "Hide to edge", "Close", etc. can't accidentally
      // initiate a drag when the user just wants to tap them.
      const target = e.target;
      if (target && typeof target.closest === "function" && target.closest("[data-no-drag]")) {
        return;
      }
      const rect = el.getBoundingClientRect();
      stateRef.current.pointerId = e.pointerId;
      stateRef.current.startX = e.clientX;
      stateRef.current.startY = e.clientY;
      stateRef.current.elemX = rect.left;
      stateRef.current.elemY = rect.top;
      stateRef.current.dragging = false;
      stateRef.current.moved = false;
      // Intentionally NOT calling setPointerCapture: capture retargets
      // subsequent pointer events to *this* element, which breaks our
      // window-level pointermove listener under Playwright + some
      // Chromium builds. The window listeners catch every move
      // anyway because they're at the window level.
    };

    const onPointerMove = (e) => {
      if (stateRef.current.pointerId !== e.pointerId) return;
      const dx = e.clientX - stateRef.current.startX;
      const dy = e.clientY - stateRef.current.startY;
      if (!stateRef.current.dragging) {
        // Wait until the user has clearly moved before stealing the gesture.
        if (Math.abs(dx) < THRESHOLD && Math.abs(dy) < THRESHOLD) return;
        stateRef.current.dragging = true;
        try { if (navigator.vibrate) navigator.vibrate(10); } catch (_) {}
        setDragging(true);
      }
      stateRef.current.moved = true;
      // Compute new absolute viewport position. Top edge is clamped at
      // 80px to keep the floating widget out of the app header zone so
      // it never covers the HW logo. Bottom edge respects the optional
      // `bottomReserve` so the widget never gets dragged behind a
      // sticky BottomNav. Other edges keep an 8px gutter.
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let x = stateRef.current.elemX + dx;
      let y = stateRef.current.elemY + dy;
      x = Math.max(8, Math.min(vw - size - 8, x));
      y = Math.max(80, Math.min(vh - size - 8 - bottomReserve, y));
      setPos({ left: x, top: y });
    };

    const onPointerUp = (e) => {
      if (stateRef.current.pointerId !== e.pointerId) return;
      const wasDragging = stateRef.current.dragging;
      const wasMoved = stateRef.current.moved;
      stateRef.current.pointerId = null;
      stateRef.current.dragging = false;
      if (wasDragging) {
        setDragging(false);
        if (wasMoved) {
          // Snap to the nearest edge so the UI feels tidy. We pick
          // the edge the widget is CLOSEST to — left, right, top, or
          // bottom — whichever is the shortest distance from the
          // widget's center. Gives the user a Spotify-like "carry it
          // anywhere on screen" feel.
          const rect = el.getBoundingClientRect();
          const vw = window.innerWidth;
          const vh = window.innerHeight;
          // Distance from each screen edge to the widget's NEAREST
          // edge (not its center). Using the widget's edge means a
          // wide mini-player flush against the screen edge reports
          // distance ≈ 0 — which is what we want for auto-tuck
          // thresholding. Using the center would always overstate the
          // distance by half the widget's width/height.
          const dLeft = rect.left;
          const dRight = vw - rect.right;
          const dTop = rect.top;
          const dBottom = vh - rect.bottom;
          // For SNAPPING we still pick the edge by the widget's center
          // so we don't snap to "top" when the widget is wider than
          // it is tall and merely floating in the middle of the screen.
          const cx = rect.left + rect.width / 2;
          const cy = rect.top + rect.height / 2;
          const sLeft = cx;
          const sRight = vw - cx;
          const sTop = cy;
          const sBottom = vh - cy;
          const sMin = Math.min(sLeft, sRight, sTop, sBottom);
          let snapped;
          let snappedEdge;
          let snappedDistance;
          if (sMin === sLeft) {
            snapped = { left: 8, top: Math.max(8, Math.min(vh - rect.height - 8 - bottomReserve, rect.top)) };
            snappedEdge = "left";
            snappedDistance = dLeft;
          } else if (sMin === sRight) {
            snapped = { right: 8, top: Math.max(8, Math.min(vh - rect.height - 8 - bottomReserve, rect.top)) };
            snappedEdge = "right";
            snappedDistance = dRight;
          } else if (sMin === sTop) {
            // Snap to top but keep the widget out of the header zone
            // (≥ 80px from the top edge) so it never covers the HW
            // logo. Without this floor, flinging the player upward
            // would land it overlapping the app brand.
            snapped = { top: 80, left: Math.max(8, Math.min(vw - rect.width - 8, rect.left)) };
            snappedEdge = "top";
            snappedDistance = dTop;
          } else {
            // Snap to bottom but respect bottomReserve so the widget
            // doesn't end up tucked behind a sticky BottomNav. With
            // bottomReserve=96 the MiniPlayer parks at bottom: 104
            // instead of bottom: 8.
            snapped = { bottom: 8 + bottomReserve, left: Math.max(8, Math.min(vw - rect.width - 8, rect.left)) };
            snappedEdge = "bottom";
            snappedDistance = dBottom;
          }
          setPos(snapped);
          try { window.localStorage.setItem(storageKey, JSON.stringify(snapped)); } catch (_) {}
          // Notify caller about which edge we snapped to — used by
          // the MiniPlayer to auto-tuck when dragged near an edge.
          try { onSnap && onSnap({ edge: snappedEdge, distance: snappedDistance }); } catch (_) {}
          // Consume the click event that would otherwise fire after a
          // drag-release so the user doesn't accidentally open the panel.
          const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          el.addEventListener("click", swallow, { capture: true, once: true });
          setTimeout(() => {
            try { el.removeEventListener("click", swallow, true); } catch (_) {}
          }, 350);
        }
      }
    };

    el.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);

    // Mouse-event fallback. Some headless test harnesses (Playwright
    // launched with certain flags) and older browsers don't deliver
    // pointer events through the React event system the way real
    // hardware does. Bridge mousedown/mousemove/mouseup back into
    // the same state machine via a synthetic-looking pointer event
    // shape so the behavior is identical end-to-end.
    const mouseEvt = (e) => ({
      pointerId: -1, // sentinel id for mouse-fallback events
      pointerType: "mouse",
      button: e.button,
      clientX: e.clientX,
      clientY: e.clientY,
    });
    const onMouseDown = (e) => {
      // Only fire if pointerdown didn't already initialize state.
      if (stateRef.current.pointerId !== null) return;
      onPointerDown(mouseEvt(e));
    };
    const onMouseMove = (e) => {
      if (stateRef.current.pointerId !== -1) return;
      onPointerMove(mouseEvt(e));
    };
    const onMouseUp = (e) => {
      if (stateRef.current.pointerId !== -1) return;
      onPointerUp(mouseEvt(e));
    };
    el.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    // Touch-event fallback. Some Android browsers / WebView builds
    // (TWA wrappers, older Chrome, Samsung Internet) drop pointer
    // events when they originate inside a region that contains an
    // <iframe> — even one with pointer-events:none. The touch event
    // model predates pointer events and works everywhere. We bridge
    // touchstart/touchmove/touchend back into the same pointer-event
    // state machine so behavior is identical.
    const synthFromTouch = (t) => ({
      pointerId: 1,
      pointerType: "touch",
      button: 0,
      clientX: t.clientX,
      clientY: t.clientY,
    });
    const onTouchStart = (e) => {
      const t = e.touches[0];
      if (!t) return;
      onPointerDown(synthFromTouch(t));
    };
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!t) return;
      // Always cancel scroll once we've crossed the threshold so the
      // page doesn't slide underneath the user's finger.
      if (stateRef.current.dragging) {
        try { e.preventDefault(); } catch (_) {}
      }
      onPointerMove(synthFromTouch(t));
    };
    const onTouchEnd = () => onPointerUp({ pointerId: 1 });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);

    // Re-clamp on resize so a position set on a wide screen still
    // sits inside the viewport when the user rotates / shrinks.
    const onResize = () => {
      setPos((prev) => {
        if (!prev) return prev;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const next = { ...prev };
        if (typeof next.left === "number") next.left = Math.max(8, Math.min(vw - size - 8, next.left));
        if (typeof next.right === "number") next.right = Math.max(8, Math.min(vw - size - 8, next.right));
        if (typeof next.top === "number") next.top = Math.max(8, Math.min(vh - size - 8, next.top));
        if (typeof next.bottom === "number") next.bottom = Math.max(8, Math.min(vh - size - 8, next.bottom));
        return next;
      });
    };
    window.addEventListener("resize", onResize);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      el.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      window.removeEventListener("resize", onResize);
    };
  }, [el, storageKey, size, onSnap]);

  const style = pos
    ? { position: "fixed", ...pos, transition: dragging ? "none" : "all 220ms ease-out", touchAction: "none" }
    : { position: "fixed", ...defaultPosition, touchAction: "none" };

  return { ref, style, dragging };
}
